// Stránka 404 (issue #33, screens/19-404.md, mockupy design/404.dc.html
// + „404 mobil.dc.html").
//
// Nativní DOM Shoptetu je jen odstavec s tlačítkem:
//   body.in-404 > #content-wrapper > .breadcrumbs
//               > main#content > .content-inner > .empty-content.empty-content-404
//                 > h1[data-testid=pageHeader] + h2 + p + p > a.btn
// Restylovat není co — celý rozcestník (hledání, dlaždice „Kam dál", poradna,
// časté odkazy, pás bestsellerů) je NÁŠ obsahový blok vložený do `.empty-content`.
// Nativní H1 se PŘESOUVÁ (ne kopíruje) a jen se mu přepíše formulace; zbytek
// nativního textu i „Zpět do obchodu" schová CSS až pod `html.bs-404-ready`,
// takže bez JS zůstane funkční nativní hláška.
//
// ODCHYLKY OD SPECKY (ověřeno na dev instanci 2026-07-29):
//   • Spec tvrdí „nativní breadcrumb tu Shoptet nerenderuje" — renderuje
//     („Domů / Stránka neexistuje"). Design ho nemá → schováváme ho CSS.
//   • Vyhledávací pole není přenesený nativní form z hlavičky (ten by hlavičce
//     chyběl a POSTuje na /action/ProductSearch/prepareString/), ale sdílené
//     `.bs-sfield` ze stránky výsledků — GET na /vyhledavani/?string=, takže
//     po vykreslení funguje i bez JS.
//   • „Nejprodávanější" blok 404 nativně nemá a Shoptet ho na téhle stránce
//     nevyrenderuje. Pás proto dotahujeme z homepage (fetch + DOMParser) a
//     bereme z něj i nadpis skupiny z administrace — vlastní popisek by lhal.
//     Když fetch selže nebo skupina neexistuje, pás se vypustí (spec to dovoluje,
//     layout na něm nestojí).
//
// Idempotentní; katalogové dlaždice se ověřují proti nativnímu menu, ať na 404
// nevede odkaz do další 404.

import { icon } from './icon.js';
import { readMenu } from './menuModel.js';
import { CONTACT, telHref } from './headerTools.js';
import { HOURS } from '../locale/cs/common.js';
import { buildAdvice } from './categoryPage.js';
import { buildSearchForm } from './searchRender.js';
import { POPULAR, searchUrl } from './searchSuggest.js';
import { initProductCards } from './productCard.js';
import { page, catalogRoot, withPages } from './pages.js';
import { saleLabel } from './campaign.js';
import L from '../locale/cs/notFoundPage.js';

const READY = 'bs-404-ready';
const SALE_PATH = page('sale');

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_CODE = L.code;
const LABEL_EYEBROW = L.eyebrow;
const LABEL_TITLE = L.title;
const LABEL_PEREX = L.perex;
const LABEL_PLACEHOLDER = L.placeholder;
const LABEL_POPULAR = L.popularLabel;
const LABEL_WHERE = L.where;
const LABEL_LINKS = L.linksTitle;
const LABEL_BEST = L.best;

// Rozcestník (§„Kam dál"). `cat: true` = katalogová dlaždice, která se ověřuje
// proti nativnímu menu; `red` = červené kolečko místo šedého.
const TILES = withPages([
  {
    href: catalogRoot('garden'),
    name: L.tiles.garden.name,
    desc: L.tiles.garden.desc,
    icon: 'leaf',
    red: true,
    cat: true,
  },
  {
    href: catalogRoot('build'),
    name: L.tiles.build.name,
    desc: L.tiles.build.desc,
    icon: 'hammer',
    cat: true,
  },
  { href: catalogRoot('workshop'), name: L.tiles.workshop.name, desc: L.tiles.workshop.desc, icon: 'wrench', cat: true },
  { href: page('brands'), name: L.tiles.brands.name, desc: L.tiles.brands.desc, icon: 'rosette' },
  {
    href: page('sale'),
    name: L.tiles.sale.name,
    desc: L.tiles.sale.desc,
    icon: 'percent',
    red: true,
    cat: true,
  },
  {
    href: page('service'),
    name: L.tiles.service.name,
    desc: L.tiles.service.desc,
    icon: 'cog',
  },
]);

// Časté odkazy (§„Pravý sloupec"). Cesty ověřené na dev instanci (všechny 200).
const LINKS = withPages([
  { href: page('orderStatus'), name: L.links.orderStatus },
  { href: page('shipping'), name: L.links.shipping },
  { href: page('claim'), name: L.links.claim },
  { href: page('withdrawal'), name: L.links.withdrawal },
  { href: page('contact'), name: L.links.contact },
]);

// Poradna — sdílená komponenta `.bs-advice`; telefon i otevírací doba jedou
// z `CONTACT` (hlavička/patička), ať se čísla nikde nerozejdou.
const ADVICE = {
  eyebrow: L.advice.eyebrow,
  title: L.advice.title,
  text: L.advice.text,
  textNoPhone: L.advice.textNoPhone,
  // kanály v pořadí chat → e-mail → telefon (buildAdvice, chatActions)
  actions: [
    { chat: '404', primary: true },
    { href: page('contact'), label: L.advice.writeQuestion, icon: 'mail' },
    { href: telHref(CONTACT.phone), label: CONTACT.phone, icon: 'phone', primary: true },
  ],
  note: HOURS.support + ' ' + CONTACT.hours,
};

// Kolik mini karet vezmeme z homepage (design má 4)
const BEST_LIMIT = 4;

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function link(href, cls) {
  const node = el('a', cls);
  node.href = href;
  return node;
}

function sectionHead(label) {
  const head = el('div', 'bs-sechead bs-sechead--listing');
  head.appendChild(el('h2', 'bs-sechead__title', label));
  head.appendChild(el('span', 'bs-sechead__line'));
  return head;
}

function isNotFound() {
  return document.body.classList.contains('in-404');
}

// ---------------------------------------------------------------------------
// Levý sloupec: hlavička chyby, hledání, rozcestník
// ---------------------------------------------------------------------------
function buildHead(main, host) {
  const head = el('div', 'bs-404__head');
  head.appendChild(el('span', 'bs-404__code', LABEL_CODE));
  head.appendChild(el('span', 'bs-404__rule'));
  head.appendChild(el('span', 'bs-404__eyebrow', LABEL_EYEBROW));
  main.appendChild(head);

  // nativní H1 se přesune (data-testid a pozice v DOM zůstávají Shoptetu)
  const h1 = host.querySelector('h1') || el('h1');
  h1.classList.add('bs-404__title');
  h1.textContent = LABEL_TITLE;
  main.appendChild(h1);

  main.appendChild(el('p', 'bs-404__perex', LABEL_PEREX));
}

function buildSearch(main) {
  main.appendChild(buildSearchForm(LABEL_PLACEHOLDER));

  const row = el('div', 'bs-404__popular');
  row.appendChild(el('span', 'bs-404__popular-label', LABEL_POPULAR));
  const chips = el('span', 'bs-404__chips');
  POPULAR.slice(0, 5).forEach(function (q) {
    const chip = link(searchUrl(q), 'bs-404__chip');
    chip.textContent = q;
    chips.appendChild(chip);
  });
  row.appendChild(chips);
  main.appendChild(row);
}

// Katalogové dlaždice jen tam, kde cesta opravdu je v nativním menu — jinak by
// rozcestník ze 404 vedl do další 404.
function knownPaths() {
  const paths = {};
  readMenu().forEach(function (root) {
    paths[root.path] = true;
  });
  return paths;
}

function buildTiles(main) {
  const known = knownPaths();
  const tiles = TILES.filter(function (t) {
    return !t.cat || known[t.href];
  });
  if (!tiles.length) return;

  main.appendChild(sectionHead(LABEL_WHERE));

  const grid = el('div', 'bs-404__tiles');
  tiles.forEach(function (t) {
    const card = link(t.href, 'bs-404__tile');
    const mark = el('span', 'bs-404__tile-icon' + (t.red ? ' bs-404__tile-icon--red' : ''));
    mark.appendChild(icon(t.icon));
    card.appendChild(mark);
    // akční nabídka nese po dobu kampaně název deštníku („Black Friday")
    card.appendChild(el('span', 'bs-404__tile-name', (t.href === SALE_PATH && saleLabel()) || t.name));
    card.appendChild(el('span', 'bs-404__tile-desc', t.desc));
    grid.appendChild(card);
  });
  main.appendChild(grid);
}

// ---------------------------------------------------------------------------
// Pravý sloupec: poradna + časté odkazy
// ---------------------------------------------------------------------------
function buildSide(side) {
  side.appendChild(buildAdvice(ADVICE));

  const box = el('div', 'bs-404__links');
  box.appendChild(el('div', 'bs-404__links-title', LABEL_LINKS));
  LINKS.forEach(function (l) {
    const row = link(l.href, 'bs-404__link');
    row.appendChild(el('span', 'bs-404__link-label', l.name));
    row.appendChild(icon('arrow-right'));
    box.appendChild(row);
  });
  side.appendChild(box);
}

// ---------------------------------------------------------------------------
// Pás „Nejprodávanější" — 404 vlastní produktový výpis nemá, dotahujeme ho
// z homepage. Nadpis bereme z administrace (nativní `.homepage-group-title`),
// ať pás neslibuje jiný výběr, než jaký ve skutečnosti ukazuje.
// ---------------------------------------------------------------------------
function pickGroup(doc) {
  const wraps = Array.prototype.slice.call(doc.querySelectorAll('.products-wrapper'));
  let fallback = null;
  for (let i = 0; i < wraps.length; i += 1) {
    const wrap = wraps[i];
    if (!wrap.querySelector('.p[data-micro="product"]')) continue;
    const head = wrap.previousElementSibling;
    const label =
      head && head.classList.contains('homepage-group-title')
        ? (head.textContent || '').replace(/\s+/g, ' ').trim()
        : '';
    if (L.bestHeadingRe.test(label)) return { wrap: wrap, label: label };
    if (!fallback) fallback = { wrap: wrap, label: label };
  }
  return fallback;
}

// Z karty homepage zůstane jen to, co mini karta ukazuje — add-to-cart form
// (i s nativním tokenem) do cizí stránky nepatří, košík řeší detail produktu.
function stripCard(card) {
  card.querySelectorAll('form, .p-tools, .p-desc, .p-code').forEach(function (node) {
    node.remove();
  });
  card.removeAttribute('style');
  return card;
}

function buildBest(host, group) {
  if (!group || document.querySelector('.bs-404__best')) return;
  const cards = Array.prototype.slice
    .call(group.wrap.querySelectorAll('.p[data-micro="product"]'))
    .slice(0, BEST_LIMIT);
  if (!cards.length) return;

  const section = el('section', 'bs-404__best');
  section.appendChild(sectionHead(group.label || LABEL_BEST));

  // `.bs-mini` = přepínač mini karty pro productCard.js (nativní kotva je
  // `.products-top`, ta tu ale není — blok stavíme my)
  const box = el('div', 'bs-mini bs-404__best-box');
  const track = el('div', 'products bs-404__best-track');
  cards.forEach(function (card) {
    const item = el('div', 'product');
    item.appendChild(stripCard(document.importNode(card, true)));
    track.appendChild(item);
  });
  box.appendChild(track);
  section.appendChild(box);
  host.appendChild(section);

  initProductCards(); // idempotentní — dorovná jen nově vložené karty
}

function loadBest(host) {
  if (!window.fetch || !window.DOMParser) return;
  fetch('/', { credentials: 'same-origin' })
    .then(function (res) {
      return res.ok ? res.text() : Promise.reject(new Error(String(res.status)));
    })
    .then(function (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      buildBest(host, pickGroup(doc));
    })
    .catch(function () {
      /* homepage nedostupná → pás se vypustí, layout na něm nestojí */
    });
}

// ---------------------------------------------------------------------------

export function initNotFoundPage() {
  if (!isNotFound()) return;
  const host = document.querySelector('.empty-content-404') || document.querySelector('.empty-content');
  if (!host || host.querySelector('.bs-404__grid')) return;

  host.classList.add('bs-404');

  // co z nativního bloku nahrazujeme (schová CSS až pod html.bs-404-ready)
  host.querySelectorAll(':scope > h2, :scope > p').forEach(function (node) {
    node.classList.add('bs-404__native');
  });

  const grid = el('div', 'bs-404__grid');
  const main = el('div', 'bs-404__main');
  const side = el('aside', 'bs-404__side');

  buildHead(main, host);
  buildSearch(main);
  buildTiles(main);
  buildSide(side);

  grid.appendChild(main);
  grid.appendChild(side);
  host.insertAdjacentElement('afterbegin', grid);

  document.documentElement.classList.add(READY);

  // pás bestsellerů až po vykreslení zbytku — je to jen doplněk
  if (window.requestIdleCallback) window.requestIdleCallback(function () { loadBest(host); });
  else window.setTimeout(function () { loadBest(host); }, 300);
}
